import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import { colors } from 'styles/colors';
import { fonts } from './fonts';
import { hexWithOpacity } from 'common/utils';

const useGlobalStyles = makeStyles((theme: Theme) => ({
  container: {
    flex: 1,
    backgroundColor: colors.topBackground,
  },
  sectionContainer: {
    backgroundColor: colors.white,
    borderRadius: '15px',
    overflow: 'hidden',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  },
  sectionHeader: {
    backgroundColor: colors.white,
    minHeight: 80,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingLeft: '20px'
  },
  sectionButton: {
    backgroundColor: colors.topBackground,
    width: 35,
    height: 35,
    borderRadius: '4px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  profileSectionButton: {
    backgroundColor: colors.topBackground,
    width: 32,
    height: 32,
    borderRadius: '4px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalTitle: {
    color: colors.primary,
    fontSize: '20px',
    fontWeight: 600,
    textAlign: 'center',
    marginTop: 10,
    marginBottom: 10,
  },
  modalContainer: {
    borderRadius: 3,
    boxShadow: '0px 12px 48px rgba(0,0,0,0.2)',
    overflow: 'hidden',
  },
  icdItemContainer: {
    border: `1px solid ${colors.border}`,
    borderRadius: '10px',
  },
  popoverIconContainer: {
    width: 25,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  popoverButtonContainer: {
    justifyContent: 'flex-start',
    minWidth: 180,
    borderRadius: '10px',
    width: '100%',
    height: 48,
  },
  cellText: {
    fontFamily: fonts.neue,
    fontWeight: 500,
    color: colors.black,
    fontSize: '15px',
  },
  tableContainer: {
    height: '100%',
    width: '100%',
    bgcolor: 'pink',
    borderRadius: '15px',
    overflow: 'hidden',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.10)',
  },
  dataGrid: {

    '& .MuiDataGrid-columnHeader .MuiDataGrid-checkboxInput.MuiCheckbox-indeterminate svg': {
      transform: 'scale(0.9)',
      transformOrigin: 'center',
    },

    '& .MuiDataGrid-checkboxInput.Mui-checked svg': {
      transform: 'scale(0.9)',
      transformOrigin: 'center',
    },

    border: 0,
    '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
      outline: 'none',
    },

    '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
      outline: 'none',
    },

    '& .MuiDataGrid-cell': {
      display: 'flex',
      alignItems: 'center',
    },

    '& .MuiDataGrid-row:hover': {
      cursor: 'pointer',
    },

    '& .row-added': {
      backgroundColor: 'rgba(56, 196, 121, 0.2)',
      transition: 'background 0.3s',
    },

    '& .MuiTablePagination-toolbar': {
      display: 'flex',
      alignItems: 'center',
      minHeight: 52,
    },
    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
      margin: 0,
      lineHeight: 'normal',
      display: 'flex',
      alignItems: 'center',
    },
    '& .MuiTablePagination-select': {
      display: 'flex',
      alignItems: 'center',
    },


  },
  textInputLabel: {
    fontSize: '15px',
    fontFamily: fonts.neue,
    fontWeight: 500,
    color: colors.black,
  },
  textInputPlaceholder: {
    fontSize: '15px',
    fontFamily: fonts.neue,
    fontWeight: 500,
    color: colors.secondary,
  },
  headerIconButton: {
    borderRadius: 8,
    minWidth: 40,
    height: 40,
    boxShadow: `0px 0px 4px ${hexWithOpacity(colors.pureBorder, 0.5)}`,
    bgcolor: 'background.paper',
    '&:hover': {
      boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.18)',
      bgcolor: 'background.paper',
    },
  },
  floatingLabel: {
    position: 'absolute',
    height: 20,
    top: -10,
    left: '10px',
    backgroundColor: colors.white,
    zIndex: 10,
    padding: '2px 5px'
  },
  floatingLabelText: {
    fontSize: '15px',
    fontFamily: fonts.neue,
    fontWeight: 500,
    color: colors.black,
    '& .MuiInputLabel-asterisk': {
      color: colors.error,
    },
  },
  aic: {
    display: 'flex',
    alignItems: 'center'
  },
  sectionPrimaryHeader: {
    backgroundColor: colors.primary,
    color: 'primary.contrastText',
    height: 80,
    pl: 3,
  },
  sectionPrimaryHeaderTitle: {
    fontSize: 18,
    fontWeight: 500,
    letterSpacing: 0.3,
    fontFamily: fonts.neue,
    color: colors.white,
    // textTransform: 'uppercase',
  },
  sidebarIconButton: {
    height: 40,
    '&:hover': {
      backgroundColor: hexWithOpacity(colors.secondary, 0.1),
    }
  },
  positionItemContainer: {
    backgroundColor: colors.white,
    minHeight: 118,
    border: `1px solid ${colors.secondary}`,
    borderRadius: '15px',
    padding: '15px',
  },
  reimbursementItemContainer: {
    backgroundColor: colors.white,
    border: `1px solid ${colors.secondary}`,
    borderRadius: '15px',
    padding: '15px',
  },
  p0: {
    padding: 0,
  },
  resultItem: {
    border: `1px solid ${colors.itemBorder}`,
    borderRadius: '10px',
    marginBottom: '10px',
    cursor: 'pointer',
    paddingLeft: '10px',
    '&:hover': {
      backgroundColor: hexWithOpacity(colors.primary, 0.1),
    },
  },
  registryItem: {
    backgroundColor: colors.white,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.10)',
    borderRadius: '15px',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: hexWithOpacity(colors.white, 0.5),
    },
  },
  guestContainer: {
    minHeight: '90vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  boxSignIn: {
    display: 'flex',
    marginTop: -74,
    width: '100%',
    height: '100vh',
    justifyContent: 'center',
    alignItems: 'center',
  },
  bigBox: {
    margin: 15,
    display: 'flex',
    justifyContent: 'center',
    marginTop: 200,
  },
  paperSignIn: {
    paddingTop: 32,
    paddingBottom: 56,
    paddingRight: 90,
    paddingLeft: 90,
    textAlign: 'center',
    maxWidth: 500,
  },
  forgetPasswordHeader: {
    color: colors.secondary,
  },
}));

export default useGlobalStyles;
